import {FLEX_WEIGHT_DEFAULT} from '@stores/catalog/flexLayout'
import {action, computed} from 'mobx'
import {BorderNode, DefaultNodeType, DockLocation, DropInfo, IDraggable, IDropTarget, IJsonRowNode, IJsonTabSetNode, ILayoutMetrics, Model, Orientation, Rect, SplitterNode, TabNode, TabSetNode} from '../'
import {FlexLayoutNode} from './FlexLayoutNode'
import {AttributeDefinitions} from '../AttributeDefinitions'
import {_} from '@api'
import {Attribute} from '../Attribute'

export class RowNode extends FlexLayoutNode<DefaultNodeType, IJsonRowNode> implements IDropTarget {
  static readonly TYPE = 'row'

  @computed get isInColumn() {
    return this.parent?.isVertical === true
  }

  @computed get tabSets() : Array<TabSetNode> {
    return this.children.filter(c => c instanceof TabSetNode) as Array<TabSetNode>
  }

  @computed get averageWeight() {
    const {tabSets} = this
    return _.sum(tabSets.map(c => c.weight)) / tabSets.length
  }

  get row() : RowNode {
    return this.parent as RowNode
  }

  @computed get shorthand() {
    const {children} = this
    let result = '', previous: FlexLayoutNode = null
    children.forEach(child => {
      if (previous instanceof TabSetNode || previous instanceof RowNode) {
        result += this.isVertical ? '/ ' : '| '
      }

      if (child.isLayoutSidebar) {
        result += 'S '
      } else if (child instanceof TabSetNode) {
        child.tabs.forEach(t => {
          result += t.shorthand
        })
        result += ' '
      } else if (child instanceof RowNode) {
        const addParens = result != ''
        if (addParens) {
          result += '('
        }

        result += child.shorthand
        if (addParens) {
          result += ') '
        }
      } else {
        throw new Error(`Unhandled case - ${child.type}`)
      }

      previous = child
    })

    return _.trimEnd(result)
  }

  /** @hidden @internal */
  static _fromJson(json: any, model: Model) {
    const newLayoutNode = new RowNode(model, json)

    json.children = json.children ?? []
    for (const jsonChild of json.children) {
      if (jsonChild.type === TabSetNode.TYPE) {
        const child = TabSetNode._fromJson(jsonChild, model)
        newLayoutNode.addChild(child)
      } else if (jsonChild.type === RowNode.TYPE) {
        const child = RowNode._fromJson(jsonChild, model)
        newLayoutNode.addChild(child)
      } else {
        console.error(`Cannot deserialize row node child from jsonChild`, jsonChild)
      }
    }

    return newLayoutNode
  }

  /** @hidden @internal */
  private static _attributeDefinitions: AttributeDefinitions = RowNode._createAttributeDefinitions()

  /** @hidden @internal */
  private static _createAttributeDefinitions(): AttributeDefinitions {
    const attributeDefinitions = new AttributeDefinitions()
    attributeDefinitions.add('type', RowNode.TYPE, true).setType(Attribute.STRING).setFixed()
    attributeDefinitions.add('id', undefined).setType(Attribute.STRING)

    attributeDefinitions.add('weight', 100).setType(Attribute.NUMBER)
    attributeDefinitions.add('width', undefined).setType(Attribute.NUMBER)
    attributeDefinitions.add('height', undefined).setType(Attribute.NUMBER)

    return attributeDefinitions
  }

  /** @hidden @internal */
  private _minHeight: number
  private _minWidth: number

  /** @hidden @internal */
  constructor(model: Model, json: any) {
    super(model)

    this._dirty = true
    this._minHeight = 0
    this._minWidth = 0
    RowNode._attributeDefinitions.fromJson(json, this._attributes)
    model._addNode(this)
  }

  @computed get isInRow() {
    return this.parent instanceof RowNode
  }

  get weight() {
    return this._attributes.weight as number
  }

  get width() {
    return this._getAttr('width') as number | undefined
  }

  get height() {
    return this._getAttr('height') as number | undefined
  }

  /** @hidden @internal */
  @action override _layout = (rect: Rect, metrics: ILayoutMetrics) => {
    if (!this._rect?.equals(rect)) {
      this._rect = rect
    }
    const pixelSize = this.rect._getSize(this.orientation)

    let totalWeight = 0
    let fixedPixels = 0
    let prefPixels = 0
    let totalPrefWeight = 0
    const drawChildren = this.drawChildren as (RowNode | TabSetNode | SplitterNode)[]

    for (const child of drawChildren) {
      const prefSize = child._getPrefSize(this.orientation)
      if (child._isFixed()) {
        if (prefSize !== undefined) {
          fixedPixels += prefSize
        }
      } else {
        if (prefSize === undefined) {
          totalWeight += child.weight
        } else {
          prefPixels += prefSize
          totalPrefWeight += child.weight
        }
      }
    }

    let resizePreferred = false
    let availablePixels = pixelSize - fixedPixels - prefPixels
    if (availablePixels < 0) {
      availablePixels = pixelSize - fixedPixels
      resizePreferred = true
      totalWeight += totalPrefWeight
    }
    // assign actual pixel sizes
    let totalSizeGiven = 0
    let variableSize = 0
    for (const child of drawChildren) {
      const prefSize = child._getPrefSize(this.orientation)
      if (child._isFixed()) {
        if (prefSize !== undefined) {
          child._setTempSize(prefSize)
        }
      } else {
        if (prefSize == null || resizePreferred) {
          if (totalWeight === 0) {
            child._setTempSize(0)
          } else {
            const minSize = child.getMinSize(this.orientation)
            const size = Math.floor(availablePixels * (child.weight / totalWeight))
            child._setTempSize(Math.max(minSize, size))
          }
          variableSize += child._getTempSize()
        } else {
          child._setTempSize(prefSize)
        }
      }

      totalSizeGiven += child._getTempSize()
    }

    // adjust sizes to exactly fit
    if (variableSize > 0) {
      while (totalSizeGiven < pixelSize) {
        for (const child of drawChildren) {
          if (!(child instanceof SplitterNode)) {
            const prefSize = child._getPrefSize(this.orientation)
            if (!child._isFixed() && (prefSize === undefined || resizePreferred) && totalSizeGiven < pixelSize) {
              child._setTempSize(child._getTempSize() + 1)
              totalSizeGiven++
            }
          }
        }
      }

      // decrease size using nodes not at there minimum
      while (totalSizeGiven > pixelSize) {
        let changed = false
        for (const child of drawChildren) {
          if (!(child instanceof SplitterNode)) {
            const minSize = child.getMinSize(this.orientation)
            const size = child._getTempSize()
            if (size > minSize && totalSizeGiven > pixelSize) {
              child._setTempSize(child._getTempSize() - 1)
              totalSizeGiven--
              changed = true
            }
          }
        }
        if (!changed) {
          // all children are at min values
          break
        }
      }

      // if still too big then simply reduce all nodes until fits
      while (totalSizeGiven > pixelSize) {
        let changed = false
        for (const child of drawChildren) {
          if (!(child instanceof SplitterNode)) {
            const size = child._getTempSize()
            if (size > 0 && totalSizeGiven > pixelSize) {
              child._setTempSize(child._getTempSize() - 1)
              totalSizeGiven--
              changed = true
            }
          }
        }
        if (!changed) {
          // all children are at 0 values
          break
        }
      }
    }

    // layout children
    let p = 0
    for (const child of drawChildren) {
      if (this.orientation === Orientation.HORZ) {
        child._layout(new Rect(this.rect.x + p, this.rect.y, child._getTempSize(), this.rect.height), metrics)
      } else {
        child._layout(new Rect(this.rect.x, this.rect.y + p, this.rect.width, child._getTempSize()), metrics)
      }
      p += child._getTempSize()
    }

    return true
  }

  /** @hidden @internal */
  _getSplitterBounds(splitterNode: SplitterNode, useMinSize: boolean = false) {
    const pBounds = [0, 0]
    const drawChildren = this.drawChildren as (RowNode | TabSetNode | SplitterNode)[]
    const p = drawChildren.indexOf(splitterNode)
    const node1 = drawChildren[p - 1]
    const node2 = drawChildren[p + 1]
    if (this.orientation === Orientation.HORZ) {
      const minSize1 = useMinSize ? node1.minWidth : 0
      const minSize2 = useMinSize ? node2.minWidth : 0
      pBounds[0] = node1.rect.x + minSize1
      pBounds[1] = node2.rect.right - splitterNode.width - minSize2
    } else {
      const minSize1 = useMinSize ? node1.minHeight : 0
      const minSize2 = useMinSize ? node2.minHeight : 0
      pBounds[0] = node1.rect.y + minSize1
      pBounds[1] = node2.rect.bottom - splitterNode.height - minSize2
    }
    return pBounds
  }

  /** @hidden @internal */
  _calculateSplit(splitter: SplitterNode, splitterPos: number) {
    let rtn
    const drawChildren = this.drawChildren as (RowNode | TabSetNode | SplitterNode)[]
    const p = drawChildren.indexOf(splitter)
    const pBounds = this._getSplitterBounds(splitter)

    const weightedLength = drawChildren[p - 1].weight + drawChildren[p + 1].weight

    const pixelWidth1 = Math.max(0, splitterPos - pBounds[0])
    const pixelWidth2 = Math.max(0, pBounds[1] - splitterPos)

    if (pixelWidth1 + pixelWidth2 > 0) {
      const weight1 = (pixelWidth1 * weightedLength) / (pixelWidth1 + pixelWidth2)
      const weight2 = (pixelWidth2 * weightedLength) / (pixelWidth1 + pixelWidth2)

      rtn = {
        node1Id: drawChildren[p - 1].id,
        weight1,
        pixelWidth1,
        node2Id: drawChildren[p + 1].id,
        weight2,
        pixelWidth2
      }
    }

    return rtn
  }

  @computed get drawChildren(): FlexLayoutNode[] {
    let r = []
    const children = this._children.filter(c => !c.hidden)

    for (let i = 0; i < children.length; i++) {
      const child = children[i] as RowNode | TabSetNode
      if (i !== 0) {
        const newSplitter = new SplitterNode(this._model)
        newSplitter._setParent(this)
        r.push(newSplitter)
      }
      r.push(child)
    }

    return r.filter(_.identity)
  }

  /** @hidden @internal */
  getMinSize(orientation: Orientation) {
    if (orientation === Orientation.HORZ) {
      return this._minWidth
    } else {
      return this._minHeight
    }
  }

  get minWidth() {
    return this._minWidth
  }

  get minHeight() {
    return this._minHeight
  }

  /** @hidden @internal */
  calcMinSize() {
    this._minHeight = 0
    this._minWidth = 0
    let first = true
    this._children.forEach((child) => {
      const c = child as RowNode | TabSetNode
      if (c instanceof RowNode) {
        c.calcMinSize()
      }
      if (this.orientation === Orientation.VERT) {
        this._minHeight += c.minHeight
        if (!first) {
          this._minHeight += this._model.splitterSize
        }
        this._minWidth = Math.max(this._minWidth, c.minWidth)
      } else {
        this._minWidth += c.minWidth
        if (!first) {
          this._minWidth += this._model.splitterSize
        }
        this._minHeight = Math.max(this._minHeight, c.minHeight)
      }
      first = false
    })
  }

  /** @hidden @internal */
  _tidy(itemsSeen: Record<string, boolean> = {}) {
    let i = 0
    const {_children, _removeChild} = this

    // Remove duplicate ids
    _children.slice().reduce((acc, node) => {
      if (acc[node.id]) {
        console.debug(`Duplicate node found in layout model - removing`, {node})
        _removeChild(node)
      }
      else {
        acc[node.id] = true
      }

      return acc
    }, itemsSeen)

    while (i < _children.length) {
      const child = _children[i]
      if (child instanceof RowNode) {
        child._tidy(itemsSeen)
        const childChildren = child.children
        if (childChildren.length === 0) {
          this._removeChild(child)
        } else if (childChildren.length === 1 && !child.id.endsWith('Sidebar')) {
          // hoist child/children up to this level

          const subchild = childChildren[0]
          this._removeChild(child)
          if (subchild instanceof RowNode) {
            let subChildrenTotal = 0
            const subChildChildren = subchild.children
            for (const ssc of subChildChildren) {
              const subsubChild = ssc as RowNode | TabSetNode
              subChildrenTotal += subsubChild.weight
            }
            for (let j = 0; j < subChildChildren.length; j++) {
              const subsubChild = subChildChildren[j] as RowNode | TabSetNode
              subsubChild._setWeight((child.weight * subsubChild.weight) / subChildrenTotal)
              this.addChild(subsubChild, i + j)
            }
          } else {
            subchild._setWeight(child.weight)
            this.addChild(subchild, i)
          }
        } else {
          i++
        }
      } else if (child instanceof TabSetNode && child.children.length === 0) {
        if (child.isEnableDeleteWhenEmpty) {
          this._removeChild(child)
          if (child === this._model.maximizedTabset) {
            this._model._setMaximizedTabset(undefined)
          }
        } else {
          i++
        }
      } else {
        i++
      }
    }

    // add tabset into empty root
    if (!TEST && // Otherwise we can't start from a blank model when writing tests
        this === this._model.root && this._children.length === 0) {
      const callback = this._model._getOnCreateTabSet()
      const child = new TabSetNode(this._model, callback ? callback() : {})
      this._model._setActiveTabset(child)
      this.addChild(child)
    }

  }

  /** @hidden @internal */
  canDrop(dragNode: FlexLayoutNode & IDraggable, x: number, y: number): DropInfo | undefined {
    const yy = y - this.rect.y
    const xx = x - this.rect.x
    const w = this.rect.width
    const h = this.rect.height
    const margin = 10 // height of edge rect
    const half = 50 // half width of edge rect
    let dropInfo

    if (this._model.isEnableEdgeDock() && this.parent === undefined) {
      // _root row
      if (x < this.rect.x + margin && yy > h / 2 - half && yy < h / 2 + half) {
        const dockLocation = DockLocation.LEFT
        const outlineRect = dockLocation.getDockRect(this.rect)
        outlineRect.width = outlineRect.width / 2
        dropInfo = new DropInfo(this, outlineRect, dockLocation, -1, 'flexlayout__outline_rect_edge')
      } else if (x > this.rect.right - margin && yy > h / 2 - half && yy < h / 2 + half) {
        const dockLocation = DockLocation.RIGHT
        const outlineRect = dockLocation.getDockRect(this.rect)
        outlineRect.width = outlineRect.width / 2
        outlineRect.x += outlineRect.width
        dropInfo = new DropInfo(this, outlineRect, dockLocation, -1, 'flexlayout__outline_rect_edge')
      } else if (y < this.rect.y + margin && xx > w / 2 - half && xx < w / 2 + half) {
        const dockLocation = DockLocation.TOP
        const outlineRect = dockLocation.getDockRect(this.rect)
        outlineRect.height = outlineRect.height / 2
        dropInfo = new DropInfo(this, outlineRect, dockLocation, -1, 'flexlayout__outline_rect_edge')
      } else if (y > this.rect.bottom - margin && xx > w / 2 - half && xx < w / 2 + half) {
        const dockLocation = DockLocation.BOTTOM
        const outlineRect = dockLocation.getDockRect(this.rect)
        outlineRect.height = outlineRect.height / 2
        outlineRect.y += outlineRect.height
        dropInfo = new DropInfo(this, outlineRect, dockLocation, -1, 'flexlayout__outline_rect_edge')
      }

      if (dropInfo !== undefined) {
        if (!dragNode._canDockInto(dragNode, dropInfo)) {
          return undefined
        }
      }
    }

    return dropInfo
  }

  /** @hidden @internal */
  @action drop = (dragNode: FlexLayoutNode & IDraggable, location: DockLocation, index: number): void => {
    const dockLocation = location

    const parent = dragNode.parent,
      {indexInParent} = dragNode

    if (parent) {
      parent._removeChild(dragNode)
    }

    if (parent !== undefined && parent!.type === TabSetNode.TYPE) {
      parent._setSelected(Math.max(0, indexInParent - 1))
    }

    if (parent !== undefined && parent!.type === BorderNode.TYPE) {
      parent._setSelected(-1)
    }

    let tabSet: TabSetNode | undefined
    if (dragNode instanceof TabSetNode) {
      tabSet = dragNode
    } else {
      const callback = this._model._getOnCreateTabSet()
      tabSet = new TabSetNode(this._model, callback ? callback(dragNode as TabNode) : {})
      tabSet.addChild(dragNode)
    }
    let size = this._children.reduce((sum, child) => {
      return sum + (child as RowNode | TabSetNode).weight
    }, 0)

    if (size === 0) {
      size = 100
    }

    tabSet._setWeight(size / 3)

    const horz = !this._model.isRootOrientationVertical

    if (horz && dockLocation === DockLocation.LEFT || !horz && dockLocation === DockLocation.TOP) {
      this.addChild(tabSet, 0)
    } else if (horz && dockLocation === DockLocation.RIGHT || !horz && dockLocation === DockLocation.BOTTOM) {
      this.addChild(tabSet)
    } else if (horz && dockLocation === DockLocation.TOP || !horz && dockLocation === DockLocation.LEFT) {
      const vrow = new RowNode(this._model, {})
      const hrow = new RowNode(this._model, {})
      hrow._setWeight(75)
      tabSet._setWeight(25)
      this._children.forEach((child) => {
        hrow.addChild(child)
      })
      this._removeAll()
      vrow.addChild(tabSet)
      vrow.addChild(hrow)
      this.addChild(vrow)
    } else if (horz && dockLocation === DockLocation.BOTTOM || !horz && dockLocation === DockLocation.RIGHT) {
      const vrow = new RowNode(this._model, {})
      const hrow = new RowNode(this._model, {})
      hrow._setWeight(75)
      tabSet._setWeight(25)
      this._children.forEach((child) => {
        hrow.addChild(child)
      })
      this._removeAll()
      vrow.addChild(hrow)
      vrow.addChild(tabSet)
      this.addChild(vrow)
    }

    this._model._setActiveTabset(tabSet)

    this._model.tidy()
  }

  /** @hidden @internal */
  @computed get asJson() {
    const json: any = {}
    RowNode._attributeDefinitions.toJson(json, this._attributes)

    json.children = []
    this._children.forEach((child) => {
      json.children.push(child.asJson)
    })

    return json
  }

  get isEnableDrop() {
    return true
  }

  /** @hidden @internal */
  _getPrefSize = (orientation: Orientation) => {
    let prefSize = this.width
    if (orientation === Orientation.VERT) {
      prefSize = this.height
    }
    return prefSize
  }

  /** @hidden @internal */
  _getAttributeDefinitions() {
    return RowNode._attributeDefinitions
  }

  /** @hidden @internal */
  @action _updateAttrs = (json: any) => {
    RowNode._attributeDefinitions.update(json, this._attributes)
  }

  /** @hidden @internal */
  static getAttributeDefinitions() {
    return RowNode._attributeDefinitions
  }

  @computed get isHorizontal() { return this.orientation.name === 'horz'}

  @computed get isVertical() { return !this.isHorizontal}

  @action addChildJson = (childJson: IJsonTabSetNode | IJsonRowNode, index?: number) => {
    //console.log(`addChildJson`, {childJson, index, parent: this})
    const {_children, model} = this
    const child = childJson.type === 'tabset' ? TabSetNode._fromJson(childJson, model) : RowNode._fromJson(childJson, model)
    child.parent = this

    if (_.isNumber(index)) {
      _children.splice(index, 0, child)
    } else {
      _children.push(child)
    }

    if (child.weight) {
      _children.forEach(c => {
        if (!c.attributes.weight) {
          c.attributes.weight = 1
        }
      })
    }

    return child
  }

  @computed get totalWeight() {
    return _.sumBy(this.children, (n => n.attributes.weight))
  }

  /**
   *
   * @param percent
   */
  normalizeWeight = (percent: number) => {
    if (percent <= 0 || percent >= 1) throw new Error(`Percent should be a number between 0 and 1`)
    // They pass in 25, they want
    // weights:  125 | 75,  percent: 25, result: x / 200 + x ===  x = (25 * 200) + 25x
    const {totalWeight} = this
    if (!totalWeight) { return percent * 100 }

    return (percent * totalWeight) / (1 - percent)
  }

  @action addTabSet = () => {
    return this.addChildJson({ type: 'tabset', weight: FLEX_WEIGHT_DEFAULT, children: []}) as TabSetNode
  }

  uncollapse = () => {
    const {weight, _setWeight} = this
    if (weight === 0) {
      _setWeight(FLEX_WEIGHT_DEFAULT)
    }
    this.row && this.row.uncollapse()
  }
}



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/model/RowNode.ts